// Contenedor de panel del editor: COLUMNA lateral en desktop, BOTTOM-SHEET en
// mobile. Copia el patrón responsive de Atlas (FormatPicker / OptionSheet /
// OmniRefAttach): en mobile se portalea a document.body, entra con
// `animate-slide-up-sheet`, tiene drag-to-dismiss y respeta la safe-area del notch.
// Un panel a la vez (lo garantiza `activeTool` en EditorShell).

import { useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useIsDesktop } from '../lib/useIsDesktop';

interface Props {
  title: string;
  onClose: () => void;
  children: ReactNode;
}

export function PanelSheet({ title, onClose, children }: Props) {
  const isDesktop = useIsDesktop();

  if (isDesktop) {
    // Desktop: columna lateral, igual que antes de mobile.
    return (
      <div className="shrink-0 w-[280px] max-w-[80vw] border-r border-neutral-200 bg-surface-card flex flex-col">
        <div className="flex items-center justify-between px-4 h-12 border-b border-neutral-100 shrink-0">
          <span className="text-[13px] font-semibold text-brand-black/90">{title}</span>
          <button
            type="button"
            onClick={onClose}
            className="w-7 h-7 rounded-full flex items-center justify-center text-neutral-500 hover:bg-neutral-100 active:scale-95 transition"
            aria-label="Cerrar"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 min-h-0 flex flex-col">{children}</div>
      </div>
    );
  }

  return createPortal(<MobileSheet title={title} onClose={onClose}>{children}</MobileSheet>, document.body);
}

function MobileSheet({ title, onClose, children }: Props) {
  const [dragY, setDragY] = useState(0);
  const startY = useRef<number | null>(null);

  const onTouchStart = (e: React.TouchEvent) => {
    startY.current = e.touches[0].clientY;
  };
  const onTouchMove = (e: React.TouchEvent) => {
    if (startY.current == null) return;
    const dy = e.touches[0].clientY - startY.current;
    if (dy > 0) setDragY(dy);
  };
  const onTouchEnd = () => {
    if (dragY > 100) onClose();
    setDragY(0);
    startY.current = null;
  };

  return (
    <>
      <div className="fixed inset-0 z-[200] bg-black/40 animate-fade-in" onClick={onClose} />
      <div
        className="fixed inset-x-0 bottom-0 z-[201] bg-surface-card rounded-t-2xl shadow-2xl flex flex-col animate-slide-up-sheet"
        style={{
          maxHeight: 'calc(100dvh - 4rem - env(safe-area-inset-top, 0px))',
          transform: dragY ? `translateY(${dragY}px)` : undefined,
          transition: dragY ? 'none' : undefined,
          paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)',
        }}
      >
        {/* Drag handle + header iOS "Cerrar · Título" (toda la zona arrastrable). */}
        <div onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd} className="shrink-0">
          <div className="py-2.5 flex justify-center">
            <div className="w-9 h-[5px] rounded-full bg-black/[0.15]" />
          </div>
          <div className="flex items-center justify-between px-4 pb-2.5 border-b border-neutral-100">
            <button type="button" onClick={onClose} className="text-[15px] text-brand-primary active:opacity-60 w-16 text-left">
              Cerrar
            </button>
            <span className="text-[15px] font-semibold text-brand-black/90">{title}</span>
            <span className="w-16" aria-hidden />
          </div>
        </div>
        <div className="flex-1 min-h-0 flex flex-col overscroll-contain">{children}</div>
      </div>
    </>
  );
}
